import * as tus from 'tus-js-client';
import { supabase } from '../supabase';
import type { ScreenRecording, ScreenRecordingMode } from '../types/screenRecording';
import { useScreenRecordingsStore } from '../stores/screenRecordingsStore';
import * as recordingStorage from './recordingStorage';

const BUCKET = 'screen-recordings';
const TUS_CHUNK_SIZE = 6 * 1024 * 1024;
const TUS_RETRY_DELAYS = [0, 3000, 5000, 10000, 20000];

/**
 * Strip codec parameters from a MIME type string.
 * Supabase Storage `allowed_mime_types` matches by exact string, so the codecs
 * suffix produced by MediaRecorder (`video/webm;codecs=vp8,opus`) causes 415.
 */
function baseMimeType(full: string): string {
  return (full || '').split(';')[0].trim() || 'video/webm';
}

interface UploadParams {
  blob: Blob;
  mimeType: string;
  durationSeconds: number;
  mode: ScreenRecordingMode;
  title?: string;
  /** Audio-only blob per trascrizione Whisper (opzionale ma raccomandato). */
  audioBlob?: Blob | null;
  audioMimeType?: string | null;
  onProgress?: (pct: number) => void;
}

interface UploadResult {
  recording: ScreenRecording;
  publicUrl: string;
}

export interface UploadCallbacks {
  onProgress?: (percent: number, bytesSent: number, bytesTotal: number) => void;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export type UploadWarningType = 'thumbnail_failed' | 'audio_failed' | 'transcribe_failed';

export interface UploadWarning {
  type: UploadWarningType;
  at: string;
  message: string;
}

/**
 * Genera una thumbnail JPEG (~10% della durata) dal Blob video lato client.
 * Restituisce un Blob JPEG, oppure null se la generazione fallisce.
 */
export async function generateThumbnail(videoBlob: Blob): Promise<Blob | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(videoBlob);
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;

    const cleanup = () => URL.revokeObjectURL(url);

    video.addEventListener('loadedmetadata', () => {
      // Su alcuni browser duration è Infinity per webm: workaround con seek a large value
      if (!Number.isFinite(video.duration) || video.duration === 0) {
        video.currentTime = 1e6;
        video.addEventListener('durationchange', () => {
          const safe = Number.isFinite(video.duration) ? video.duration : 0;
          video.currentTime = Math.max(0.1, safe * 0.1);
        }, { once: true });
      } else {
        video.currentTime = Math.max(0.1, video.duration * 0.1);
      }
    });

    video.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas');
        const w = video.videoWidth || 1280;
        const h = video.videoHeight || 720;
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          cleanup();
          resolve(null);
          return;
        }
        ctx.drawImage(video, 0, 0, w, h);
        canvas.toBlob((b) => {
          cleanup();
          resolve(b);
        }, 'image/jpeg', 0.8);
      } catch (err) {
        console.error('[thumbnail] generate error:', err);
        cleanup();
        resolve(null);
      }
    });

    video.addEventListener('error', () => {
      console.error('[thumbnail] video load error');
      cleanup();
      resolve(null);
    });

    video.src = url;
    video.load();
  });
}

async function getOrgAndUserIds(): Promise<{ orgId: string; userId: string }> {
  const { data: u } = await supabase.auth.getUser();
  if (!u.user) throw new Error('User not authenticated');
  const { data: profile, error } = await supabase
    .from('user_profiles')
    .select('organization_id')
    .eq('id', u.user.id)
    .single();
  if (error || !profile?.organization_id) throw new Error('Organization not found');
  return { orgId: profile.organization_id, userId: u.user.id };
}

async function uploadVideoTus(params: {
  blob: Blob;
  mimeType: string;
  objectName: string;
  onProgress?: (pct: number) => void;
}): Promise<void> {
  const { blob, mimeType, objectName, onProgress } = params;

  // Refresh token per coprire upload lunghi (token Supabase scade in 1h)
  await supabase.auth.refreshSession();
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sessione non valida');

  const projectUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const endpoint = `${projectUrl}/storage/v1/upload/resumable`;

  await new Promise<void>((resolve, reject) => {
    const upload = new tus.Upload(blob, {
      endpoint,
      retryDelays: TUS_RETRY_DELAYS,
      headers: {
        authorization: `Bearer ${session.access_token}`,
        'x-upsert': 'true',
      },
      uploadDataDuringCreation: true,
      removeFingerprintOnSuccess: true,
      metadata: {
        bucketName: BUCKET,
        objectName,
        contentType: baseMimeType(mimeType),
        cacheControl: '3600',
      },
      chunkSize: TUS_CHUNK_SIZE,
      onError: (err) => reject(err),
      onProgress: (sent, total) => {
        if (onProgress && total > 0) onProgress(Math.min(100, (sent / total) * 100));
      },
      onSuccess: () => resolve(),
    });
    upload.start();
  });
}

/**
 * LEGACY (pre-OPFS): upload completo da Blob in RAM.
 * Crea la draft row, fa l'upload TUS, thumb + audio, segna ready.
 * Mantenuto come fallback per ambienti senza OPFS o per consumer non ancora
 * migrati al flusso recordingId-based. Task 6 sostituisce il consumer principale
 * (`uploadQueueStore`) con il nuovo `uploadRecording(recordingId)`.
 */
export async function uploadRecordingLegacy(params: UploadParams): Promise<UploadResult> {
  const { blob, mimeType, durationSeconds, mode, title, audioBlob, audioMimeType, onProgress } = params;
  const { orgId, userId } = await getOrgAndUserIds();

  const recId = crypto.randomUUID();
  const ext = mimeType.startsWith('video/webm') ? 'webm' : 'mp4';
  const videoPath = `${orgId}/${userId}/${recId}.${ext}`;
  const thumbnailPath = `${orgId}/${userId}/${recId}.jpg`;
  const audioExt = audioMimeType?.includes('mp4') ? 'm4a' : 'webm';
  const audioPath = audioBlob ? `${orgId}/${userId}/${recId}.audio.${audioExt}` : null;

  // 1) Thumbnail (best effort) — fatta PRIMA della draft per coerenza
  const thumbBlob = await generateThumbnail(blob).catch(() => null);

  // 2) Draft row
  const store = useScreenRecordingsStore.getState();
  const draft = await store.createDraft({
    recording_mode: mode,
    storage_path: videoPath,
    thumbnail_path: thumbBlob ? thumbnailPath : null,
    audio_storage_path: audioPath,
    mime_type: baseMimeType(mimeType),
    duration_seconds: durationSeconds,
    size_bytes: blob.size,
    title,
  });

  try {
    // 3) Upload video via TUS (resumable)
    await uploadVideoTus({
      blob,
      mimeType: baseMimeType(mimeType),
      objectName: videoPath,
      onProgress,
    });

    // 4) Upload thumbnail (se disponibile)
    if (thumbBlob) {
      const { error: thumbErr } = await supabase.storage
        .from(BUCKET)
        .upload(thumbnailPath, thumbBlob, {
          contentType: 'image/jpeg',
          upsert: true,
          cacheControl: '3600',
        });
      if (thumbErr) {
        console.warn('[upload] thumbnail upload failed (non-fatal):', thumbErr);
      }
    }

    // 5) Upload audio dedicato per trascrizione (se disponibile)
    if (audioBlob && audioPath && audioMimeType) {
      const { error: audioErr } = await supabase.storage
        .from(BUCKET)
        .upload(audioPath, audioBlob, {
          contentType: baseMimeType(audioMimeType),
          upsert: true,
          cacheControl: '3600',
        });
      if (audioErr) {
        console.warn('[upload] audio upload failed (non-fatal, no transcript):', audioErr);
      }
    }

    // 6) Mark as ready
    await store.markReady(draft.id);

    // 7) Trigger trascrizione in background (non blocca il flow utente)
    if (audioPath) {
      supabase.functions.invoke('transcribe-screen-recording', {
        body: { recording_id: draft.id },
      }).catch((err) => {
        console.warn('[upload] transcribe trigger failed (non-fatal):', err);
      });
    }

    // 8) Re-fetch per ottenere la riga aggiornata con public_token
    const fresh = await store.fetchById(draft.id);
    if (!fresh) throw new Error('Recording non trovato dopo upload');

    const publicUrl = `${window.location.origin}/v/${fresh.public_token}`;
    return { recording: fresh, publicUrl };
  } catch (err) {
    await store.markFailed(draft.id).catch(() => {});
    throw err;
  }
}

/**
 * Incrementa upload_attempts in modo best-effort (read+write, race accettata —
 * il valore è informativo, non safety-critical).
 */
async function bumpUploadAttempts(recordingId: string): Promise<void> {
  const { data: currentRow } = await supabase
    .from('screen_recordings')
    .select('upload_attempts')
    .eq('id', recordingId)
    .maybeSingle();
  await supabase
    .from('screen_recordings')
    .update({ upload_attempts: ((currentRow?.upload_attempts as number | null) ?? 0) + 1 })
    .eq('id', recordingId);
}

/**
 * Upload TUS resumable di una registrazione il cui blob risiede su OPFS.
 *
 * - Legge video/audio/thumb da `recordingStorage` (OPFS), NON da RAM
 * - Fingerprint stabile `recording-{uuid}` → resume cross-refresh via `urlStorage`
 *   (default localStorage di tus-js-client)
 * - `findPreviousUploads` + `resumeFromPreviousUpload` prima di `start()`
 * - On success: upload thumb + audio non-fatali (popolano `upload_warnings`),
 *   trigger transcribe, segna `status='ready'`, POI elimina OPFS folder
 * - On error: persiste `last_upload_error` + `last_upload_error_at` per UI
 *
 * NOTA: assume che la DB row esista già in stato `uploading` (creata dal recorder
 * onstop in Task 7). Non crea la draft autonomamente.
 */
export async function uploadRecording(
  recordingId: string,
  callbacks: UploadCallbacks = {}
): Promise<tus.Upload> {
  // Multi-tab guard: best-effort check via navigator.locks.query.
  // Prevents the common case of two tabs both clicking "Riprova" on the same recording.
  // Race condition is theoretically possible but acceptable — this is a UX safeguard, not a hard lock.
  if (typeof navigator !== 'undefined' && navigator.locks?.query) {
    try {
      const lockName = `recording-upload-${recordingId}`;
      const state = await navigator.locks.query();
      const isHeld = state.held?.some((l) => l.name === lockName);
      if (isHeld) {
        throw new Error(`Upload già in corso in un'altra scheda per ${recordingId.slice(0, 8)}`);
      }
    } catch (e) {
      // Re-throw only our own error; swallow any navigator.locks environment failures
      if (e instanceof Error && e.message.startsWith('Upload già in corso')) throw e;
    }
  }

  const meta = await recordingStorage.readMeta(recordingId);
  if (!meta) throw new Error(`Recording meta non trovata in OPFS per ${recordingId}`);

  const opfsFile = await recordingStorage.readVideo(recordingId);
  if (!opfsFile) throw new Error(`Recording video non trovato in OPFS per ${recordingId}`);

  // Force-flatten the OPFS File into a fresh Blob with the original mimeType.
  // OPFS returns a `File` which can confuse some TUS slicing paths and lose the
  // original mimeType; re-materializing via arrayBuffer + new Blob yields a
  // single contiguous Blob with the correct codec metadata.
  const videoBuffer = await opfsFile.arrayBuffer();
  const videoBlob = new Blob([videoBuffer], { type: meta.mimeType });

  console.log('[upload] OPFS read', {
    recordingId,
    opfsFileSize: opfsFile.size,
    opfsFileType: opfsFile.type || '(empty)',
    bufferByteLength: videoBuffer.byteLength,
    blobSize: videoBlob.size,
    blobType: videoBlob.type,
    metaMimeType: meta.mimeType,
  });

  await bumpUploadAttempts(recordingId);

  // Token fresco per upload lungo
  await supabase.auth.refreshSession();
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sessione non valida');

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  const ext = meta.mimeType.startsWith('video/webm') ? 'webm' : 'mp4';
  const storagePath = `${meta.organizationId}/${meta.userId}/${recordingId}.${ext}`;
  const thumbnailPath = `${meta.organizationId}/${meta.userId}/${recordingId}.jpg`;
  const audioPath = `${meta.organizationId}/${meta.userId}/${recordingId}.audio.webm`;

  const upload = new tus.Upload(videoBlob, {
    endpoint: `${supabaseUrl}/storage/v1/upload/resumable`,
    retryDelays: TUS_RETRY_DELAYS,
    chunkSize: TUS_CHUNK_SIZE,
    uploadDataDuringCreation: true,
    removeFingerprintOnSuccess: true,
    fingerprint: () => Promise.resolve(`recording-${recordingId}`),
    metadata: {
      bucketName: BUCKET,
      objectName: storagePath,
      contentType: baseMimeType(meta.mimeType),
      cacheControl: '3600',
    },
    headers: {
      authorization: `Bearer ${session.access_token}`,
      'x-upsert': 'true',
    },
    onError: async (error) => {
      const message = (error as Error)?.message ?? String(error);
      await supabase
        .from('screen_recordings')
        .update({
          last_upload_error: message,
          last_upload_error_at: new Date().toISOString(),
        })
        .eq('id', recordingId)
        .then(() => undefined, () => undefined);
      callbacks.onError?.(error as Error);
    },
    onProgress: (bytesSent, bytesTotal) => {
      const percent = bytesTotal > 0 ? (bytesSent / bytesTotal) * 100 : 0;
      callbacks.onProgress?.(percent, bytesSent, bytesTotal);
    },
    onSuccess: async () => {
      try {
        const warnings: UploadWarning[] = [];

        // Thumbnail (non-fatal)
        try {
          const thumbBlob = await recordingStorage.readThumbnail(recordingId);
          if (thumbBlob) {
            const { error: thumbErr } = await supabase.storage
              .from(BUCKET)
              .upload(thumbnailPath, thumbBlob, {
                contentType: 'image/jpeg',
                upsert: true,
                cacheControl: '3600',
              });
            if (thumbErr) {
              warnings.push({
                type: 'thumbnail_failed',
                at: new Date().toISOString(),
                message: thumbErr.message,
              });
            }
          }
        } catch (e) {
          warnings.push({
            type: 'thumbnail_failed',
            at: new Date().toISOString(),
            message: e instanceof Error ? e.message : String(e),
          });
        }

        // Audio dedicato (non-fatal)
        try {
          const audioBlob = await recordingStorage.readAudio(recordingId);
          if (audioBlob) {
            const { error: audErr } = await supabase.storage
              .from(BUCKET)
              .upload(audioPath, audioBlob, {
                contentType: 'audio/webm',
                upsert: true,
                cacheControl: '3600',
              });
            if (audErr) {
              warnings.push({
                type: 'audio_failed',
                at: new Date().toISOString(),
                message: audErr.message,
              });
            }
          }
        } catch (e) {
          warnings.push({
            type: 'audio_failed',
            at: new Date().toISOString(),
            message: e instanceof Error ? e.message : String(e),
          });
        }

        // Mark ready + persist warnings + clear error fields
        await supabase
          .from('screen_recordings')
          .update({
            status: 'ready',
            upload_warnings: warnings,
            last_upload_error: null,
            last_upload_error_at: null,
          })
          .eq('id', recordingId);

        // Trigger trascrizione (non-fatal)
        try {
          await supabase.functions.invoke('transcribe-screen-recording', {
            body: { recording_id: recordingId },
          });
        } catch (e) {
          const { data: rowNow } = await supabase
            .from('screen_recordings')
            .select('upload_warnings')
            .eq('id', recordingId)
            .maybeSingle();
          const existing = (rowNow?.upload_warnings as UploadWarning[] | null) ?? [];
          await supabase
            .from('screen_recordings')
            .update({
              upload_warnings: [
                ...existing,
                {
                  type: 'transcribe_failed',
                  at: new Date().toISOString(),
                  message: e instanceof Error ? e.message : String(e),
                },
              ],
            })
            .eq('id', recordingId);
        }

        // Cleanup OPFS SOLO dopo ack DB
        await recordingStorage.deleteRecording(recordingId).catch((e) => {
          console.warn('[upload] OPFS cleanup failed (non-fatal):', e);
        });

        callbacks.onSuccess?.();
      } catch (e) {
        callbacks.onError?.(e instanceof Error ? e : new Error(String(e)));
      }
    },
  });

  // Resume previous upload se trovato (urlStorage default = localStorage)
  const previousUploads = await upload.findPreviousUploads();
  if (previousUploads.length > 0) {
    upload.resumeFromPreviousUpload(previousUploads[0]);
  }
  upload.start();

  return upload;
}

/**
 * Pulisce gli error fields e ri-invoca `uploadRecording`. Usato dal pannello
 * "failed" per il retry manuale dell'utente.
 */
export async function retryUpload(
  recordingId: string,
  callbacks: UploadCallbacks = {}
): Promise<tus.Upload> {
  await supabase
    .from('screen_recordings')
    .update({ last_upload_error: null, last_upload_error_at: null })
    .eq('id', recordingId);
  return uploadRecording(recordingId, callbacks);
}
